import API from 'core/api'
import { IS_OPEN_LINK, STORE_ROOT_PROPS } from 'core/store/global'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAsync } from 'react-async'
import { useRecoilValue } from 'recoil'
import styled from 'styled-components'
import { getLocation, requestBridge, throttle } from 'core/util/default'
import ScheduleFeed from 'components/Schedule/ScheduleFeed'
import ScheduleList from 'components/Schedule/ScheduleList'
import ScheduleImage from 'components/Schedule/ScheduleImage'
import { useHistory } from 'react-router-dom'
import PageHeader from 'components/Page/PageHeader'
import ButtonBack from 'components/Button/ButtonBack'
import Loading from 'components/Loading/Loading'
import PageContent from 'components/Page/PageContent'
import { useTranslation } from 'react-i18next'
import { BRIDGE, ONLY_POST_MSG_PARTNERS } from 'core/constants/bridge'
import { getConfig, getPartnerId } from 'core/singleton'

import { useLocation } from 'react-router-dom'
import { GAHelper } from 'core/ga'
import { GA_EVENT } from 'core/ga/enums'
import { LINK_MOVE_TYPE } from 'core/constants'
import { STORE_AIRBRIDGE, STORE_MARKETING_GTM } from 'core/store/analysis'
import { getUrlQueryParameter, moveToLink, parseObjectToQueryString } from 'core/util/url'
import { filterObject } from 'core/util/object'
import { getExecutorForPartner } from 'core/util/partner'
import useHandleAlarm from 'core/hooks/useHandleAlarm'
import { CONFIRM_ALARM } from 'core/store/alarm'

interface PageScheduleProps {}

const PageSchedule: React.FC<PageScheduleProps> = () => {
  const { t } = useTranslation()
  const root = useRecoilValue(STORE_ROOT_PROPS)
  const isOpenLink = useRecoilValue(IS_OPEN_LINK)
  const airbridge = useRecoilValue(STORE_AIRBRIDGE)
  const marketingGTM = useRecoilValue(STORE_MARKETING_GTM)
  const confirmAlarm = useRecoilValue(CONFIRM_ALARM)
  const { alarm } = getConfig()
  const partnerId = getPartnerId()
  const [nextKey, setNextKey] = useState<string | undefined>('init')
  const [schedule, setSchedule] = useState<ScheduleItem[]>([])
  const pageScheduleRef = useRef<HTMLDivElement>(null)
  const query = getUrlQueryParameter()
  const location = useLocation()
  const history = useHistory()

  const { data, isLoading, reload } = useAsync({
    promiseFn: API.getSchedule,
    nextKey,
    limit: 10,
  })

  /**
   * @name EventHandler
   **/

  const handleClickSchedule = useCallback(
    ({ item }: { item: ScheduleItem }) =>
      () => {
        if (!item.outputPath) return

        if (marketingGTM?.checkAvailability()) {
          const queryStringPrefix = item.outputPath.indexOf('?') >= 0 ? '&' : '?'
          item.outputPath = item.outputPath.concat(
            `${queryStringPrefix}_gl=${marketingGTM.getGl()}`,
          )
        }

        const utmObject = filterObject(query, (k) => Boolean(k.startsWith('utm')))
        const params: { broadcastUrl?: string; broadcastId?: string; [k: string]: unknown } = {
          broadcastUrl: item.outputPath,
          ...utmObject,
        }
        if (ONLY_POST_MSG_PARTNERS.includes(partnerId)) {
          params.broadcastId = item.broadcastId
        }

        if (getExecutorForPartner(partnerId)?.reqMoveBroadcastBridge?.(item.outputPath)) {
          return
        }
        if (root?.bridge || window[BRIDGE.BROADCAST]) {
          requestBridge(BRIDGE.BROADCAST, params)
        } else {
          const queryString = parseObjectToQueryString({
            npc: 'nextlive',
            ...filterObject(query, (k) => Boolean(k.startsWith('utm'))),
          })
          moveToLink({
            airbridge,
            link: `${item.outputPath}${queryString}`,
            type: isOpenLink ? LINK_MOVE_TYPE.REPLACE : LINK_MOVE_TYPE.REDIRECT,
          })
        }
      },
    [airbridge, isOpenLink, marketingGTM, partnerId, query, root?.bridge],
  )

  /**
   * @name 알림기능
   */
  const { handleClickAlarm } = useHandleAlarm()

  const handleClickBack = useCallback(() => {
    new GAHelper().sendEvent(GA_EVENT.CLICK_BACK, {
      partnersId: partnerId,
      location: getLocation(location),
    })

    history.goBack()
  }, [history, partnerId, location])

  const handleLoadMore = throttle(
    useCallback(() => {
      const { scrollY, innerHeight, document } = window
      const nextKey = data && data.nextKey && JSON.stringify(data.nextKey)
      if (document.body.scrollHeight - scrollY - 250 < innerHeight && !isLoading) {
        if (nextKey) {
          reload()
        }
      }
    }, [data, reload, isLoading]),
    1000,
  )

  /**
   * @name DOMElement
   **/
  const scheduleList = useMemo(() => {
    return schedule.map((item) => {
      const {
        thumbnailList,
        broadcastId,
        broadcastName: title,
        shopName,
        programmingStartDt: startTime,
        partnerProfileImageUrl: shopLogo,
        etcLinkUrl,
        etcLinkOpenType,
        usePurchaseEvent: isShowPurchaseAuthBadge,
        useNotification,
      } = item
      const isConfirmAlarm = confirmAlarm.some(
        ({ broadcastId: confirmBroadcastId }) => broadcastId === confirmBroadcastId,
      )
      const representThumbnail = thumbnailList.filter((thumbnail) => thumbnail.isRepresentative)[0]
      const useAlarm = alarm?.use || etcLinkOpenType !== 'none'
      const LinkButtonTagAttributes = {
        onClick: handleClickSchedule({ item }),
      }

      if (root?.scheduleType === 'feed') {
        return (
          <ScheduleFeed
            key={broadcastId}
            thumbnail={representThumbnail.thumbnailUrl}
            title={title}
            broadcastId={broadcastId}
            startTime={startTime}
            shopName={shopName}
            shopLogo={shopLogo}
            useAlarm={useAlarm}
            etcLinkUrl={etcLinkUrl}
            etcLinkOpenType={etcLinkOpenType}
            LinkButtonTagAttributes={LinkButtonTagAttributes}
            isShowPurchaseAuthBadge={isShowPurchaseAuthBadge}
            onClickAlarm={() => handleClickAlarm({ broadcastId, ScheduleItem: item })}
            useNotification={useNotification}
            isConfirmAlarm={isConfirmAlarm}
          />
        )
      } else if (root?.scheduleType === 'list') {
        return (
          <ScheduleList
            key={broadcastId}
            thumbnail={representThumbnail.thumbnailUrl}
            title={title}
            broadcastId={broadcastId}
            startTime={startTime}
            shopName={shopName}
            shopLogo={shopLogo}
            useAlarm={useAlarm}
            etcLinkUrl={etcLinkUrl}
            etcLinkOpenType={etcLinkOpenType}
            LinkButtonTagAttributes={LinkButtonTagAttributes}
            isShowPurchaseAuthBadge={isShowPurchaseAuthBadge}
            onClickAlarm={() => handleClickAlarm({ broadcastId, ScheduleItem: item })}
            useNotification={useNotification}
            isConfirmAlarm={isConfirmAlarm}
          />
        )
      }

      return (
        <ScheduleImage
          key={broadcastId}
          thumbnail={representThumbnail.thumbnailUrl}
          title={title}
          broadcastId={broadcastId}
          useAlarm={useAlarm}
          etcLinkUrl={etcLinkUrl}
          etcLinkOpenType={etcLinkOpenType}
          LinkButtonTagAttributes={LinkButtonTagAttributes}
          isShowPurchaseAuthBadge={isShowPurchaseAuthBadge}
          onClickAlarm={() => handleClickAlarm({ broadcastId, ScheduleItem: item })}
          useNotification={useNotification}
          isConfirmAlarm={isConfirmAlarm}
        />
      )
    }, [])
  }, [
    schedule,
    confirmAlarm,
    alarm?.use,
    handleClickSchedule,
    root?.scheduleType,
    handleClickAlarm,
  ])

  const info = useMemo(() => {
    if (data && data.totalCount) {
      return t('schedules', { count: data.totalCount })
    }
  }, [data, t])

  /**
   * @name LifeCycleHook
   **/

  useEffect(() => {
    const { innerHeight } = window
    if (pageScheduleRef.current) {
      if (nextKey === 'init') return
      if (nextKey && innerHeight > pageScheduleRef.current.clientHeight) {
        reload()
      }
    }
  }, [nextKey, reload])

  useEffect(() => {
    if (data && data.items) {
      setSchedule((prev) => [...prev, ...data.items])
      const nextKey = data && data.nextKey
      setNextKey(nextKey)
    }
  }, [data])

  useEffect(() => {
    window.addEventListener('scroll', handleLoadMore)
    return () => {
      window.removeEventListener('scroll', handleLoadMore)
    }
  }, [handleLoadMore])

  return (
    <StyledPageSchedule ref={pageScheduleRef}>
      <PageHeader message="라이브 미리보기" id="sf_scheduleAll">
        <ButtonBack onClick={handleClickBack} />
      </PageHeader>
      <PageContent info={info} className={root?.scheduleType}>
        {scheduleList}
      </PageContent>
      {nextKey && <Loading isLoading />}
    </StyledPageSchedule>
  )
}

const StyledPageSchedule = styled.div`
  .image,
  .feed {
    > div {
      padding-left: 16px;
      margin-bottom: 40px;
      display: flex;
      flex-direction: column;
      align-items: center;
      .schedule_wrapper {
        width: 100%;
        padding-top: 75%;
        height: unset;
        position: relative;
        img {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          height: 100%;
        }
      }
    }
    .info {
      width: 100%;
    }
  }

  .list {
    > div {
      padding-left: 16px;
      margin-bottom: 20px;
      display: flex;
      flex-direction: row;
      align-items: center;
      .schedule_wrapper {
        height: unset;
        position: relative;
        width: 50%;
        padding-top: 32.5%;
        img {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          height: 100%;
          width: 100%;
        }
      }
    }
  }

  @media (max-width: 768px) {
    .image,
    .feed {
      > div {
        width: 100%;
      }
    }
    .list {
      > div {
        width: 100%;
      }
      .info {
        width: 65%;
      }
    }
  }
  @media (min-width: 769px) and (max-width: 1200px) {
    .image,
    .feed {
      > div {
        width: 50%;
      }
    }
    .list {
      > div {
        width: 50%;
      }
      .info {
        width: 50%;
      }
    }
  }
  @media (min-width: 1201px) {
    .image,
    .feed {
      > div {
        width: 33.3%;
      }
    }
    .list {
      > div {
        width: 33.3%;
      }
      .info {
        width: 50%;
      }
    }
  }
`
export default PageSchedule
